import { CourseStatus } from '../../../API';
import {
  Course,
  CourseRegistration,
  CourseRegistrationStatus,
} from '../../../models';

type SkillType = {
  name: string,
  selected: boolean,
};

export type CoursesWithRegistrationInfo = Course & {
  status?: CourseRegistrationStatus,
  registrationId?: string,
};
/**
 *
 *
 * @class CourseList
 */
class CourseService {
  public courses: Course[];

  /**
   * Creates an instance of CourseService.
   * @memberof CourseService
   */
  constructor() {
    this.courses = [];
  }

  /**
   *
   *
   * @param {*} courses
   * @memberof CourseService
   */
  public setCourses(courses: Course[]): void {
    this.courses = courses;
  }

  /**
   *
   *
   * @return {*} an array of skills.
   * Each skill having a name
   * and a selected value
   * ie: {
   *  name: 'photography',
   *  selected: true,
   * }
   * @memberof CourseService
   */
  public getSkills(): SkillType[] {
    const allSkillsWithDuplicates = [] as string[];
    this.courses.forEach((course) => {
      if (course?.categories?.[0]) {
        allSkillsWithDuplicates.push(course.categories[0]);
      }
    });
    const allSkills = Array.from(new Set(allSkillsWithDuplicates));
    const sortedSkills = allSkills.sort();
    const reducedSkills = sortedSkills.reduce((
      acc: SkillType[], cur: string,
    ) => {
      const skill = {
        name: cur,
        selected: true,
      };
      // by default first skill is selected
      // if (!index) {
      //   skill.selected = true;
      // }
      acc.push(skill);
      return acc;
    }, []);
    return reducedSkills;
  }

  /**
   *
   * Adds a status and registrationId
   * key to the course if course registration
   * exists.
   * @param {*} registrations
   * @return {*}
   * @memberof CourseService
   */
  public mergeCoursesWithRegistrations(
    registrations: CourseRegistration[],
  ): CoursesWithRegistrationInfo[] {
    const coursesWithRegistrationStatus: Array<CoursesWithRegistrationInfo> = [];
    this.courses.forEach((course) => {
      const registeredCourse = registrations
        .filter((registration) => registration.courseID === course.id)[0];

      if (!registeredCourse) {
        // Ignore courses with status PROCESSING
        if (!course?.status || course?.status === CourseStatus.AVAILABLE) {
          coursesWithRegistrationStatus.push({
            ...course,
            status: null,
          });
        }
      } else {
        coursesWithRegistrationStatus.push({
          ...course,
          status: registeredCourse.status as CourseRegistrationStatus,
          registrationId: registeredCourse.id,
        });
      }
    });
    return coursesWithRegistrationStatus;
  }

  /**
   *
   *
   * @param {*} searchTerm
   * @return {*} courses that match with the searchTerm
   * @memberof CourseService
   */
  public filterCoursesBySearchTerm(searchTerm: string): Course[] {
    const coursesReducedBySearchTerm = this.courses.reduce(
      (acc: Course[],
        cur: Course) => {
        if (cur.courseName.toLowerCase().includes(searchTerm)) {
          acc.push(cur);
        }
        if (cur.categories.includes(searchTerm)) {
          acc.push(cur);
        }
        return acc;
      }, [],
    );
    return coursesReducedBySearchTerm;
  }

  /**
   *
   * filters courses by skills that are selected.
   * @param {*} skills - all skills
   * @memberof CourseService
   */
  public filterCoursesBySelectedSkills(skills: SkillType[])
  : Course[] {
    const selectedSkills = skills
      .filter((skill) => skill.selected)
      .map((skill) => skill.name);
    if (selectedSkills.length) {
      const coursesReducedByFilter = this.courses.reduce(
        (acc: Course[],
          cur: Course) => {
          cur.categories.forEach((category) => {
            if (category && selectedSkills.includes(category)) {
              acc.push(cur);
            }
          });
          return acc;
        }, [],
      );
      return coursesReducedByFilter;
    }
    return [];
  }
}

export default CourseService;
